import { getCurrencySymbol } from "./currencyUtils";

export interface Rates {
  USD_to_ZAR?: number;
  EUR_to_ZAR?: number;
}

export interface ConvertedPrice {
  zar: number | null;
  usd: number | null;
  eur: number | null;
}

/**
 * Convert an amount expressed in `baseCurrency` into all three display currencies.
 * The base currency itself is always returned as-is; the others are derived via the
 * ZAR pivot rates supplied by the get-exchange-rates function.
 */
export function convertPrice(
  amount: number,
  baseCurrency: string | null | undefined,
  rates: Rates | undefined
): ConvertedPrice {
  const base = (baseCurrency || "ZAR").toUpperCase();
  const usdRate = rates?.USD_to_ZAR;
  const eurRate = rates?.EUR_to_ZAR;

  let zar: number | null = null;
  if (base === "ZAR") zar = amount;
  else if (base === "USD") zar = usdRate ? amount * usdRate : null;
  else if (base === "EUR") zar = eurRate ? amount * eurRate : null;

  return {
    zar,
    usd: base === "USD" ? amount : zar !== null && usdRate ? zar / usdRate : null,
    eur: base === "EUR" ? amount : zar !== null && eurRate ? zar / eurRate : null,
  };
}

const fmt = (value: number, digits = 2) =>
  value.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** Format an amount in its own currency, e.g. "$2,950.00" or "R 54,575.00". */
export function formatInCurrency(amount: number, currency: string | null | undefined, digits = 2) {
  const code = (currency || "ZAR").toUpperCase();
  const symbol = getCurrencySymbol(code);
  return code === "ZAR" ? `${symbol} ${fmt(amount, digits)}` : `${symbol}${fmt(amount, digits)}`;
}

/**
 * Build the "≈ R 54,575.00 ZAR / €2,543.00 EUR" line for the currencies other
 * than the base one. Returns null when no rate is available.
 */
export function formatEquivalents(
  amount: number,
  baseCurrency: string | null | undefined,
  rates: Rates | undefined,
  options: { withCodes?: boolean; digits?: number } = {}
): string | null {
  const { withCodes = true, digits = 2 } = options;
  const base = (baseCurrency || "ZAR").toUpperCase();
  const converted = convertPrice(amount, base, rates);

  const parts: string[] = [];
  (["ZAR", "USD", "EUR"] as const).forEach((code) => {
    if (code === base) return;
    const value = code === "ZAR" ? converted.zar : code === "USD" ? converted.usd : converted.eur;
    if (value === null || !isFinite(value)) return;
    parts.push(`${formatInCurrency(value, code, digits)}${withCodes ? ` ${code}` : ""}`);
  });

  if (parts.length === 0) return null;
  return `≈ ${parts.join(" / ")}`;
}
